/**
 * sl-apue: SeatAddonStore — GraphQL data access for seat addon operations
 *
 * Uses WorkspaceServerService (not GraphQLService directly) because this store
 * is registered in WorkspaceScope. GraphQLService lives in ServerScope and is
 * not accessible from WorkspaceScope — the server.gql() proxy must be used.
 */
import {
  memberQuotaInfoQuery,
  purchaseExtraSeatsMutation,
  removeSeatAddonMutation,
  updateSeatCountMutation,
  workspaceSeatAddonQuery,
} from '@affine/graphql';
import { Store } from '@toeverything/infra';

import type { WorkspaceServerService } from '../services/workspace-server';

export class SeatAddonStore extends Store {
  constructor(private readonly workspaceServerService: WorkspaceServerService) {
    super();
  }

  private get server() {
    if (!this.workspaceServerService.server) {
      throw new Error('Server not bound to WorkspaceServerService');
    }
    return this.workspaceServerService.server;
  }

  async fetchSeatAddon(workspaceId: string, abortSignal?: AbortSignal) {
    const data = await this.server.gql({
      query: workspaceSeatAddonQuery,
      variables: { workspaceId },
      context: { signal: abortSignal },
    });
    return data.workspaceSeatAddon;
  }

  async fetchMemberQuotaInfo(workspaceId: string, abortSignal?: AbortSignal) {
    const data = await this.server.gql({
      query: memberQuotaInfoQuery,
      variables: { workspaceId },
      context: { signal: abortSignal },
    });
    return data.memberQuotaInfo;
  }

  async mutatePurchaseExtraSeats(
    workspaceId: string,
    quantity: number,
    withdrawalConsent: boolean
  ) {
    const data = await this.server.gql({
      query: purchaseExtraSeatsMutation,
      variables: { workspaceId, quantity, withdrawalConsent },
    });
    return data.purchaseExtraSeats;
  }

  async mutateUpdateSeatCount(
    workspaceId: string,
    quantity: number,
    withdrawalConsent: boolean
  ) {
    const data = await this.server.gql({
      query: updateSeatCountMutation,
      variables: { workspaceId, quantity, withdrawalConsent },
    });
    return data.updateSeatCount;
  }

  async mutateRemoveSeatAddon(workspaceId: string) {
    const data = await this.server.gql({
      query: removeSeatAddonMutation,
      variables: { workspaceId },
    });
    return data.removeSeatAddon;
  }
}
